/*
 * History panel and Versions dialog (W2-10 projects-data). Design tokens only; the current entry has a marker icon
 * and screen-reader text, undone entries are dimmed AND labelled, never by colour alone.
 */

/* ---------------------------------------------------------------- panel */

.panel {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  background: var(--color-surface-1);
  color: var(--color-text);
}

.header {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: var(--space-2) var(--space-3);
  padding: var(--space-2) var(--space-3);
  border-bottom: var(--border-width) solid var(--color-divider);
}

.summary {
  color: var(--color-text-muted);
  font-size: var(--text-xs);
}

.headerActions {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  margin-left: auto;
}

.note {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  margin: 0;
  padding: var(--space-1) var(--space-3);
  background: var(--color-info-muted);
  color: var(--color-text);
  font-size: var(--text-xs);
}

.list {
  flex: 1;
  min-height: 0;
}

.hint {
  margin: 0;
  padding: var(--space-1) var(--space-3);
  border-top: var(--border-width) solid var(--color-divider);
  color: var(--color-text-subtle);
  font-size: var(--text-2xs);
}

.row {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  height: 100%;
  padding: 0 var(--space-2);
  font-size: var(--text-sm);
  min-width: 0;
}

.row[data-current] {
  font-weight: var(--weight-semibold);
}

.row[data-undone] {
  color: var(--color-text-disabled);
  font-style: italic;
}

.marker {
  display: inline-flex;
  width: 14px;
  color: var(--color-accent);
}

.label {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.tag {
  color: var(--color-text-subtle);
  font-size: var(--text-2xs);
}

.time {
  flex: none;
  color: var(--color-text-muted);
  font-size: var(--text-xs);
  font-style: normal;
}

.divider {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  height: 100%;
  padding: 0 var(--space-3);
  color: var(--color-text-subtle);
  font-size: var(--text-2xs);
}

.divider::before,
.divider::after {
  content: '';
  flex: 1;
  border-top: var(--border-width) dashed var(--color-border);
}

/* ---------------------------------------------------------------- versions dialog */

.versions {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}

.muted {
  margin: 0;
  color: var(--color-text-muted);
  font-size: var(--text-sm);
}

.error {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  margin: 0;
  color: var(--color-danger);
  font-size: var(--text-sm);
}

.checkpointForm {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: var(--space-2) var(--space-3);
}

.checkpointLabel {
  font-size: var(--text-sm);
  font-weight: var(--weight-medium);
}

.input {
  flex: 1 1 220px;
  min-width: 0;
  height: var(--control-h-md);
  padding: 0 var(--space-2);
  border: var(--border-width) solid var(--color-border-input);
  border-radius: var(--radius-sm);
  background: var(--color-surface-sunken);
  color: var(--color-text);
  font: inherit;
}

.input:focus-visible {
  outline: var(--focus-ring-width) solid var(--color-focus-ring);
  outline-offset: var(--focus-ring-offset);
}

.budget {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}

.versionList {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  max-height: 46vh;
  overflow: auto;
}

.version {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  padding: var(--space-2) var(--space-3);
  border: var(--border-width) solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-surface-1);
}

.version > svg {
  flex: none;
  color: var(--color-text-muted);
}

.version[data-kind='checkpoint'] > svg,
.version[data-kind='export'] > svg {
  color: var(--color-accent);
}

.version[data-current] {
  border-color: var(--color-accent-border);
}

.versionText {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}

.versionTitle {
  font-weight: var(--weight-semibold);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.versionMeta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-1) var(--space-3);
  color: var(--color-text-muted);
  font-size: var(--text-xs);
}

.kindChip,
.keptChip,
.currentChip {
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  padding: 0 var(--space-2);
  border-radius: var(--radius-pill);
  background: var(--color-surface-3);
  color: var(--color-text);
}

.keptChip {
  background: var(--color-accent-muted);
}

.currentChip {
  background: var(--color-success-muted);
}

.versionActions {
  display: flex;
  gap: var(--space-2);
}

/* ---- naming a version in place ---- */

.notice {
  display: flex;
  align-items: flex-start;
  gap: var(--space-2);
  margin: 0;
  padding: var(--space-2) var(--space-3);
  border-radius: var(--radius-sm);
  background: var(--color-info-muted);
  color: var(--color-text);
  font-size: var(--text-sm);
}

.notice > svg {
  flex: none;
  margin-top: 2px;
}

.version[data-naming] {
  align-items: flex-start;
}

.nameForm {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  min-width: 0;
}

.nameLabel {
  font-size: var(--text-sm);
  font-weight: var(--weight-medium);
}

.nameRow {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: var(--space-2);
}
